import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import {
  CLEAR_HOLIDAY,
  CLEAR_HOLIDAY_DATES,
  CLEAR_HOLIDAYS,
  FLAG_GETTING_HOLIDAY_DATES,
  FLAG_GETTING_HOLIDAYS,
  FLAG_HOLIDAY_ACTIVITY,
  GET_HOLIDAY,
  GET_HOLIDAY_DATES,
  GET_HOLIDAYS,
} from '../types';
import { HOLIDAY_ENDPOINT } from '../../config/endpoints';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  DEFAULT_DELETE_CONFIG,
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';
import { HOLIDAY_PATH } from '../../config/paths';

const flagGettingHolidays = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_HOLIDAYS,
    payload: flag,
  });

const getHolidays = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingHolidays(true));
    const query = tableFilters;
    const url = `${HOLIDAY_ENDPOINT}?${QueryString.stringify(query)}`;
    const response = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(response, null, dispatch);
    const holidays = await response.json();
    dispatch({
      type: GET_HOLIDAYS,
      payload: holidays,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingHolidays(false));
  }
};

const clearHolidays = () => (dispatch) =>
  dispatch({
    type: CLEAR_HOLIDAYS,
  });

const flagHolidayActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_HOLIDAY_ACTIVITY,
    payload: flag,
  });

const getHoliday =
  async ({ holidayId }) =>
  async (dispatch) => {
    try {
      dispatch(flagHolidayActivity(true));
      const url = `${HOLIDAY_ENDPOINT}/${holidayId}`;
      const response = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(response, null, dispatch);
      const holiday = await response.json();
      dispatch({
        type: GET_HOLIDAY,
        payload: holiday,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagHolidayActivity(false));
    }
  };

const clearHoliday = () => (dispatch) =>
  dispatch({
    type: CLEAR_HOLIDAY,
  });

const postHolidayList = (holidayList) => async (dispatch) => {
  try {
    dispatch(flagHolidayActivity(true));

    const url = `${HOLIDAY_ENDPOINT}/bulk`;
    const response = await fetch(url, {
      ...DEFAULT_POST_CONFIG,
      body: JSON.stringify(holidayList),
    });
    await isErrorResponse(response, null, dispatch);
    const result = await response.json();

    toastr.success(
      'Éxito',
      `${result.imported} feriado(s) registrado(s)${
        result.skipped > 0 ? `, ${result.skipped} omitido(s) por duplicado` : ''
      }.`,
    );

    dispatch(push(HOLIDAY_PATH));
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagHolidayActivity(false));
  }
};

const putHoliday =
  async (holidayId, { holidayDate, name, description }) =>
  async (dispatch) => {
    try {
      dispatch(flagHolidayActivity(true));
      const payload = {
        holidayDate,
        name,
        description,
      };
      const url = `${HOLIDAY_ENDPOINT}/${holidayId}`;
      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });
      await isErrorResponse(response, null, dispatch);
      const holiday = await response.json();

      dispatch(push(`${HOLIDAY_PATH}/${holiday.id}`));
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagHolidayActivity(false));
    }
  };

const deleteHoliday = (holidayId) => async (dispatch) => {
  try {
    dispatch(flagHolidayActivity(true));
    const url = `${HOLIDAY_ENDPOINT}/${holidayId}`;
    const response = await fetch(url, {
      method: 'DELETE',
      ...DEFAULT_DELETE_CONFIG,
    });
    await isErrorResponse(response, null, dispatch);
    dispatch(push(HOLIDAY_PATH));
    toastr.success('Éxito', 'El feriado ha sido eliminado.');
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagHolidayActivity(false));
  }
};

const postImportHolidays = (file) => async (dispatch) => {
  try {
    dispatch(flagHolidayActivity(true));

    const formData = new FormData();
    formData.append('file', file);

    const url = `${HOLIDAY_ENDPOINT}/import-data`;
    const response = await fetch(url, {
      ...DEFAULT_POST_CONFIG,
      headers: {},
      body: formData,
    });
    await isErrorResponse(response, null, dispatch);
    const result = await response.json();

    toastr.success(
      'Éxito',
      `${result.imported} feriado(s) registrado(s)${
        result.skipped > 0 ? `, ${result.skipped} omitido(s) por duplicado` : ''
      }.`,
    );

    return result;
  } catch ({ message }) {
    toastr.error('Error', message);
    return null;
  } finally {
    dispatch(flagHolidayActivity(false));
  }
};

const postExportHolidaysExcel = () => async (dispatch) => {
  try {
    dispatch(flagHolidayActivity(true));
    const url = `${HOLIDAY_ENDPOINT}/export-data`;
    const response = await fetch(url, { ...DEFAULT_POST_CONFIG });
    await isErrorResponse(response, null, dispatch);
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'Plantilla-Feriados.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagHolidayActivity(false));
  }
};

const flagGettingHolidayDates = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_HOLIDAY_DATES,
    payload: flag,
  });

const getHolidayDates =
  ({ fromDateString, toDateString }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingHolidayDates(true));
      const url = `${HOLIDAY_ENDPOINT}/dates?${QueryString.stringify({
        fromDateString,
        toDateString,
      })}`;
      const response = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(response, null, dispatch);
      const dates = await response.json();
      dispatch({
        type: GET_HOLIDAY_DATES,
        payload: dates,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingHolidayDates(false));
    }
  };

const clearHolidayDates = () => (dispatch) =>
  dispatch({
    type: CLEAR_HOLIDAY_DATES,
  });

export {
  flagGettingHolidays,
  getHolidays,
  clearHolidays,
  flagHolidayActivity,
  getHoliday,
  clearHoliday,
  postHolidayList,
  putHoliday,
  deleteHoliday,
  postImportHolidays,
  getHolidayDates,
  clearHolidayDates,
  postExportHolidaysExcel
};
